/**
 * Light/dark theme hook (U3).
 *
 * State lives in `<html data-theme="...">` so a single rule in globals.css
 * can swap the entire token set. Defaults to "dark" (Capture's native look);
 * preference persists in localStorage and is restored on mount.
 */
import { useCallback, useEffect, useState } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "capture.theme";

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === "light" ? "light" : "dark";
}

function applyThemeToDocument(theme: Theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

/**
 * Apply the stored theme once at app bootstrap (called from main.tsx before
 * render). F-071: routes mounted outside <AppShell/> (e.g. /verify/:documentId)
 * never render ThemeToggle — the only useTheme consumer — so without this a
 * direct Verification Station load ignored the persisted "light" preference.
 */
export function initTheme(): void {
  applyThemeToDocument(readStoredTheme());
}

export function useTheme(): {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
} {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    applyThemeToDocument(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Private-mode Safari / blocked storage — no-op; the in-memory state
      // still flips correctly for the current session.
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  return { theme, setTheme, toggleTheme };
}
